import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { apiClient, API_BASE_URL, setAuthToken, safeRequest } from '../services/httpClient';

// iOS WebView detection
const isIOSWebView = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isCapacitor = window.Capacitor !== undefined;
  return isIOS || isCapacitor;
};

// Storage helper to handle Safari's private mode and restrictions
const storageHelper = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('localStorage not available, trying sessionStorage');
      try {
        return sessionStorage.getItem(key);
      } catch (e2) {
        console.error('No storage available');
        return null;
      }
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('localStorage not available, trying sessionStorage');
      try {
        sessionStorage.setItem(key, value);
        return true;
      } catch (e2) {
        console.error('No storage available');
        return false;
      }
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage not available, trying sessionStorage');
      try {
        sessionStorage.removeItem(key);
      } catch (e2) {
        console.error('No storage available');
      }
    }
  }
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(storageHelper.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);
  
  // Track if component is mounted (prevents setState on unmounted component)
  const isMountedRef = useRef(true);

  // Use centralized API base URL (never empty)
  const backendUrl = API_BASE_URL;
  const isIOS = isIOSWebView();

  // Load user from token on mount - iOS-safe implementation
  useEffect(() => {
    isMountedRef.current = true;
    
    const loadUser = async () => {
      // Reset network error state
      if (isMountedRef.current) setNetworkError(false);

      console.log('[AUTH][BOOT] start');
      console.log(`[AUTH][BOOT] tokenKey=auth_token present=${token ? 'yes' : 'no'}`);

      // ── CAS A: No token → skip /auth/me entirely ──
      if (!token) {
        console.log('[AUTH][BOOT] no token → anonymous state');
        if (isMountedRef.current) setLoading(false);
        return;
      }

      // ── CAS B & C: Token exists → try /auth/me ──
      try {
        console.log('[AUTH][BOOT] calling /auth/me');
        setAuthToken(token);
          
        const response = await apiClient.get('/api/auth/me', {
          timeout: isIOS ? 45000 : 15000
        });
          
        if (isMountedRef.current && response?.data) {
          console.log('[AUTH][BOOT] user loaded OK');
          setUser(response.data);
            
          // V1.4: Restore user's language preference from backend on initial load
          if (response.data.language) {
            const userLang = response.data.language === 'en' ? 'en' : 'fr';
            const currentLang = localStorage.getItem('hearen_lang');
            if (currentLang !== userLang) {
              localStorage.setItem('hearen_lang', userLang);
              window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: userLang } }));
              console.log('[AUTH][BOOT] language restored:', userLang);
            }
          }
        }
      } catch (error) {
        if (!isMountedRef.current) return;

        // iOS: Handle cancelled requests gracefully
        if (error.isIOSAbort || error.code === 'REQUEST_CANCELLED') {
          console.warn('[AUTH][BOOT] iOS request cancelled, keeping token');
          setNetworkError(true);
          if (isMountedRef.current) setLoading(false);
          return;
        }

        const status = error.response?.status || error.status;
        const hasResponse = !!error.response;

        if (hasResponse && (status === 401 || status === 403)) {
          // ── CAS B: Server responded 401/403 → token invalid/expired ──
          console.log(`[AUTH][BOOT] token invalid (status=${status}) → purge + anonymous`);
          setAuthToken(null);
          storageHelper.removeItem('auth_token');
          setToken(null);
        } else if (!hasResponse) {
          // ── CAS C: No response at all → real network/WebView problem ──
          console.error(`[AUTH][BOOT] NETWORK ERROR (no response) → message=${error.message} code=${error.code}`);
          setNetworkError(true);
        } else {
          // Other server error with response (5xx etc.)
          console.warn(`[AUTH][BOOT] server error (status=${status}) → keeping token, flagging network`);
          setNetworkError(true);
        }
      }
      
      // CRITICAL: Always set loading to false to prevent blue screen
      if (isMountedRef.current) {
        setLoading(false);
      }
    };
    
    loadUser();
    
    // Cleanup: mark as unmounted to prevent state updates
    return () => {
      isMountedRef.current = false;
    };
  }, [token, backendUrl, isIOS]);

  const register = async (email, password, firstName, lastName, language = 'fr') => {
    try {
      const response = await apiClient.post('/api/auth/register', {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        language
      });
      
      const { access_token, user } = response.data;
      
      // CRITICAL iOS FIX: Inject token into HTTP client IMMEDIATELY
      // This prevents race condition where pages make API calls before useEffect runs
      setAuthToken(access_token);
      console.log('[AuthContext] Token injected immediately after register');
      
      storageHelper.setItem('auth_token', access_token);
      setToken(access_token);
      setUser(user);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const login = async (email, password) => {
    try {
      console.log('[AuthContext] Login attempt to:', API_BASE_URL + '/api/auth/login');
      
      // iOS-safe request
      const response = await apiClient.post('/api/auth/login', {
        email,
        password
      }, {
        timeout: isIOS ? 45000 : 30000
      });
      
      // Handle null response (iOS cancelled request)
      if (!response) {
        console.warn('[AuthContext] Login request returned null (possibly cancelled)');
        return {
          success: false,
          error: 'La requête a été interrompue. Veuillez réessayer.'
        };
      }
      
      const { access_token, user } = response.data;
      
      if (!access_token || !user) {
        console.error('❌ Missing access_token or user in response');
        console.error('   access_token:', access_token);
        console.error('   user:', user);
        return { 
          success: false, 
          error: 'Invalid response from server' 
        };
      }
      
      try {
        const saved = storageHelper.setItem('auth_token', access_token);
        if (!saved) {
          console.error('❌ Failed to save token to storage');
          return {
            success: false,
            error: 'Impossible de sauvegarder la session. Veuillez vérifier les paramètres de confidentialité de votre navigateur (Safari > Préférences > Confidentialité).'
          };
        }
        const verification = storageHelper.getItem('auth_token');
      } catch (e) {
        console.error('❌ Failed to save to storage:', e);
        return {
          success: false,
          error: 'Impossible de sauvegarder la session. Essayez en navigation normale (pas privée).'
        };
      }
      
      // CRITICAL iOS FIX: Inject token into HTTP client IMMEDIATELY
      // This prevents race condition where pages make API calls before useEffect runs
      setAuthToken(access_token);
      console.log('[AuthContext] Token injected immediately after login');
      
      setToken(access_token);
      setUser(user);
      
      // V1.4: Restore user's language preference from backend
      if (user.language) {
        const userLang = user.language === 'en' ? 'en' : 'fr';
        localStorage.setItem('hearen_lang', userLang);
        // Dispatch event to update all components using useLanguage hook
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: userLang } }));
        console.log('[AuthContext] Language restored from user profile:', userLang);
      }
      
      
      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Login error:', error);
      console.error('   Error message:', error.message);
      console.error('   Error response:', error.response?.data);
      console.error('   Error status:', error.response?.status || error.status);
      
      // iOS: Handle InvalidStateError / cancelled requests
      if (error.isIOSAbort || error.code === 'REQUEST_CANCELLED' || error.message?.includes('invalid state')) {
        return {
          success: false,
          error: 'La requête a été interrompue. Veuillez réessayer.'
        };
      }
      
      // Network errors
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network')) {
        return {
          success: false,
          error: 'Erreur réseau. Vérifiez votre connexion internet.'
        };
      }
      
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Échec de la connexion' 
      };
    }
  };

  const logout = () => {
    // CRITICAL iOS FIX: Clear token from HTTP client IMMEDIATELY
    setAuthToken(null);
    console.log('[AuthContext] Token cleared from HTTP client');
    
    storageHelper.removeItem('auth_token');
    // Remove old localStorage user_id if exists
    storageHelper.removeItem('user_id');
    setToken(null);
    setUser(null);
  };

  const updateUserLanguage = async (language) => {
    if (user) {
      // Update local state immediately
      setUser({ ...user, language });
      
      // Update backend using apiClient (iOS-safe)
      try {
        await apiClient.patch('/api/me/lang', { lang: language });
        console.log('[AuthContext] Language updated successfully');
      } catch (error) {
        console.error('[AuthContext] Error updating language:', error);
      }
    }
  };

  const value = {
    user,
    token,
    loading,
    networkError, // iOS: expose network error state for retry logic
    isAuthenticated: !!user,
    register,
    login,
    logout,
    updateUserLanguage
  };

  // CRITICAL iOS FIX: Gate render until auth is initialized
  // This prevents pages from making API calls before setAuthToken() has run
  if (loading) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
